import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, useTheme, Avatar } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  selectMainStrategy,
  selectOwnerStrategies,
} from "../../../../Redux/Reducers/strategyStoreSlice";

const formatReturn = (val) => {
  if (val == null) return "—";
  const sign = val >= 0 ? "+" : "";
  return `${sign}${(val).toFixed(2)}%`;
};

const StrategyOwner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const mainStrategy = useSelector(selectMainStrategy);
  const ownerStrategies = useSelector(selectOwnerStrategies);
  const owner = mainStrategy?.owner;
  const ownerName = owner?.name || "Strategy Owner";
  const ownerTitle = owner?.title;
  const ownerSince = owner?.since;
  const publishedCount = (ownerStrategies?.length ?? 0);

  const ownerSubtitle = [
    ownerTitle,
    ownerSince && `Strategist since ${ownerSince}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: `1px solid ${theme.palette.divider}`,
        mt: 2,
      }}
    >
      {/* Owner header */}
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
          <PersonIcon />
        </Avatar>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block" }}
          >
            Strategy Owner
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {ownerName}
          </Typography>
          {ownerSubtitle && (
            <Typography variant="caption" color="text.secondary">
              {ownerSubtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Strategy profile */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CheckCircleOutlineIcon sx={{ color: "primary.main", fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Strategy profile
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          {mainStrategy?.description || "No description available."}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {publishedCount} published {publishedCount === 1 ? "strategy" : "strategies"}
        </Typography>
      </Box>

      {/* Other strategies */}
      {ownerStrategies?.length > 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}
            >
              Other Strategies
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {ownerStrategies.length} available
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{
              maxHeight: "calc(7 * 77px)",
              overflowY: "auto",
              pr: ownerStrategies.length > 7 ? 0.5 : 0,
            }}
          >
            {ownerStrategies.map((s) => (
              <Box
                key={s.slug}
                onClick={() => navigate(`/store/strategy/${s.ownerSlug}/${s.slug}`)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {s.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    View strategy details
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={s.periodReturn >= 0 ? "success.main" : "error.main"}
                  >
                    {formatReturn(s.periodReturn)}
                  </Typography>
                  <OpenInNewIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default StrategyOwner;

